import { useEffect, useRef } from 'react';
import { Loader2, Mic, Square } from 'lucide-react';
import { formatSpeechRecordingElapsed } from '../../../hooks/useSpeechRecording';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import { syncSpeechSnakeGeometry } from './liksChatComposerSpeechGeometry';
import type { LiksChatComposerSpeechInput } from './LiksChatComposer.types';
import './LiksChatComposerSpeechButton.css';

export type LiksChatComposerSpeechButtonProps = {
  speechInput: LiksChatComposerSpeechInput;
  isSpeechRecording: boolean;
  isSpeechRequesting: boolean;
  isSpeechTranscribing: boolean;
  speechElapsedMs: number;
  disabled: boolean;
  onToggle: () => void;
};

export function LiksChatComposerSpeechButton({
  speechInput,
  isSpeechRecording,
  isSpeechRequesting,
  isSpeechTranscribing,
  speechElapsedMs,
  disabled,
  onToggle,
}: LiksChatComposerSpeechButtonProps) {
  const speechButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const button = speechButtonRef.current;
    if (button == null || !isSpeechRecording) {
      return;
    }

    syncSpeechSnakeGeometry(button);
    const observer = new ResizeObserver(() => {
      syncSpeechSnakeGeometry(button);
    });
    observer.observe(button);
    return () => observer.disconnect();
  }, [isSpeechRecording, speechElapsedMs]);

  return (
    <LiksTooltip
      content={
        isSpeechRecording ? speechInput.stopLabel : speechInput.startLabel
      }
      placement="top-end"
    >
      <button
        ref={speechButtonRef}
        type="button"
        className={[
          'liks-chat-composer__speech',
          isSpeechRecording && 'liks-chat-composer__speech--recording',
          isSpeechRequesting && 'liks-chat-composer__speech--requesting',
          isSpeechTranscribing && 'liks-chat-composer__speech--transcribing',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={
          isSpeechRecording ? speechInput.stopLabel : speechInput.startLabel
        }
        aria-pressed={isSpeechRecording}
        disabled={
          isSpeechRequesting ||
          isSpeechTranscribing ||
          (!isSpeechRecording && disabled)
        }
        onClick={onToggle}
      >
        <svg
          className="liks-chat-composer__speech-snake"
          viewBox="0 0 36 36"
          aria-hidden
        >
          <rect
            className="liks-chat-composer__speech-snake-path"
            x="0.5"
            y="0.5"
            width="35"
            height="35"
            rx="7.5"
            ry="7.5"
            pathLength="100"
          />
        </svg>
        {isSpeechRecording && (
          <span
            className="liks-chat-composer__speech-timer"
            aria-live="polite"
            aria-label={`${speechInput.recordingLabel} ${formatSpeechRecordingElapsed(speechElapsedMs)}`}
          >
            {formatSpeechRecordingElapsed(speechElapsedMs)}
          </span>
        )}
        <span className="liks-chat-composer__speech-face">
          {isSpeechRecording ? (
            <Square
              className="liks-chat-composer__speech-icon liks-chat-composer__speech-icon--stop"
              strokeWidth={2}
              aria-hidden
            />
          ) : isSpeechTranscribing || isSpeechRequesting ? (
            <Loader2
              className="liks-chat-composer__speech-icon liks-chat-composer__speech-icon--spin"
              strokeWidth={2}
              aria-hidden
            />
          ) : (
            <Mic
              className="liks-chat-composer__speech-icon"
              strokeWidth={2}
              aria-hidden
            />
          )}
        </span>
      </button>
    </LiksTooltip>
  );
}
